import { Button } from "@components";
import { useEffect, useMemo, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "../../ComboFilters";
import { Numbers } from "shared/utils/Numbers";
import {
  formatByType,
  formatNumberWithOneDecimal,
} from "pages/Reports/Reports";
import { If } from "shared/components/control-statements";

export default function CouponsInPeriod({
  data = [],
  isCashback,
  isBeforeNovember,
  hasUnitsReportFilters,
}) {
  const [selectedOption, setSelectedOption] = useState({
    name: "Faturamento",
    id: "FATURAMENTO",
  });
  const [selectedOption2, setSelectedOption2] = useState({
    name: isCashback ? "Valor" : "Pontos",
    id: "total",
  });

  useEffect(() => {
    if (isBeforeNovember) {
      setSelectedOption({
        name: "Custos",
        id: "CUSTOS",
      });
    }
  }, [isBeforeNovember]);

  const _data = {
    FATURAMENTO: {
      series: ["Faturamento bruto", "Faturamento líquido"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.total?.value || 0),
        data?.map((d) => d.total_with_discounts?.value || 0),
      ],
    },
    QTD_USOS: {
      series: ["Qtd. Usos"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.uses?.value || 0)],
    },
    TICKET_MEDIO: {
      series: ["Ticket médio bruto", "Ticket médio líquido"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.average_ticket?.value || 0),
        data?.map((d) => d.average_ticket_with_discounts?.value || 0),
      ],
    },
    CUSTOS: {
      series: ["Custos com disparos", "Descontos totais"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.send_cost?.value || 0),
        data?.map((d) => d.discounts?.value || 0),
      ],
    },
    TEMPO_MEDIO_USO: {
      series: ["Tempo médio p/ uso"],
      value_type: "float",
      colors: ["#f288a4"],
      data: [data.map((d) => d.average_time_to_use?.value || 0)],
    },
    ROI_CUPOM: {
      series: ["ROI"],
      value_type: "float",
      colors: ["#f288a4"],
      data: [data.map((d) => d.roi?.value || 0)],
    },
    QTD_AQUISICOES: {
      series: ["Qtd. de aquisições"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.added?.value || 0)],
    },
    TX_CONV_PONTOS: {
      series: ["Taxa de conversão (Pontos x Aquisições)"],
      value_type: "percentage",
      colors: ["#f288a4"],
      data: [data.map((d) => d.use_ratio_1?.value || 0)],
    },
    TX_EXP_PONTOS: {
      series: ["Taxa de expiração (Pontos x Expiração)"],
      value_type: "percentage",
      colors: ["#f288a4"],
      data: [data.map((d) => d.expired_ratio_1?.value || 0)],
    },
    TX_CONV_AQUISICOES: {
      series: ["Taxa de conversão (Aquisições x Usos)"],
      value_type: "percentage",
      colors: ["#f288a4"],
      data: [data.map((d) => d.use_ratio?.value || 0)],
    },
    TX_EXP_AQUISICOES: {
      series: ["Taxa de expiração (Aquisições x Expiração)"],
      value_type: "percentage",
      colors: ["#f288a4"],
      data: [data.map((d) => d.expired_ratio?.value || 0)],
    },
    POINTS: {
      series: ["Pontos gastos c/ aquisições"],
      value_type: "float",
      colors: ["#f288a4"],
      data: [data.map((d) => d.points?.value)],
    },
    expired: {
      series: ["Expirado"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.expired?.[selectedOption2.id] || 0)],
    },
    received: {
      series: ["Recebido"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.received?.[selectedOption2.id] || 0)],
    },
    used: {
      series: ["Usado"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.used?.[selectedOption2.id] || 0)],
    },
    chargeback: {
      series: ["Estornado"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.chargeback?.[selectedOption2.id] || 0)],
    },
  };

  const totalData = [];

  const getDataByIndex = (index) => {
    return data[index];
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("coupons-in-period-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const selectedOptionId = selectedOption.id;

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            const item = getDataByIndex(index);

            if (
              ["used", "expired", "received", "chargeback"].includes(
                selectedOptionId
              )
            )
              return "";

            return `
              <div>
              ${
                selectedOption.id === "QTD_USOS"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Qtd. de usos: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${item.uses.value || 0}
                    </span>  
                  </div>
                </div>`
                  : ""
              }

              ${
                selectedOption.id === "TX_CONV_PONTOS"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Tx. de conv. (Pontos x Aquisições)
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Numbers.formatPercent(item.use_ratio_1.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }
              ${
                selectedOption.id === "TX_EXP_PONTOS"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Tx. de exp. (Pontos x Aquisições)
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Numbers.formatPercent(item.expired_ratio_1.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }
              ${
                selectedOption.id === "TX_CONV_AQUISICOES"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Tx. de conv. (Aquisições x Usos)
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Numbers.formatPercent(item.use_ratio.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }
              ${
                selectedOption.id === "TX_EXP_AQUISICOES"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Tx. de exp. (Aquisições x Expiração)
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Numbers.formatPercent(item.expired_ratio.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }

                            <div>
              ${
                selectedOption.id === "QTD_AQUISICOES"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Qtd. de aquisições: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Numbers.formatFloat(item.added?.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }
              
                            <div>
              ${
                selectedOption.id === "CUSTO_DISPAROS"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Custo com disparos 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Currency.formatCurrency(item.send_cost?.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }

                            <div>
              ${
                selectedOption.id === "POINTS"
                  ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Pontos gastos com aquisições:
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
                      ${Numbers.formatFloat(item.points?.value || 0)}
                    </span>  
                  </div>
                </div>`
                  : ""
              }

                            ${
                              selectedOption.id === "FATURAMENTO"
                                ? `                 <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Faturamento bruto:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.total?.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Faturamento líquido:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(
                          +item.total_with_discounts?.value
                        )} 
                      </span>  
                    </div>`
                                : ""
                            }

                            ${
                              selectedOption.id === "CUSTOS"
                                ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#8a8af5;font-weight:700">
                        Custo total:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(
                          +item.send_cost?.value + item.discounts?.value
                        )} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Custo com disparos:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.send_cost?.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Custo com descontos:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.discounts?.value)} 
                      </span>  
                    </div>`
                                : ""
                            }

                                          ${
                                            selectedOption.id ===
                                            "DESCONTOS_TOTAIS"
                                              ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Descontos totais: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
${Currency.formatCurrency(item.discounts.value)}
                    </span>  
                  </div>
                </div>`
                                              : ""
                                          }

                                                        ${
                                                          selectedOption.id ===
                                                          "FATURAMENTO_LIQUIDO"
                                                            ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Faturamento líquido: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
${Currency.formatCurrency(item.total_with_discounts?.value)}
                    </span>  
                  </div>
                </div>`
                                                            : ""
                                                        }

                                                                      ${
                                                                        selectedOption.id ===
                                                                        "TICKET_MEDIO"
                                                                          ? `<div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Ticket médio bruto:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.average_ticket?.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Ticket médio líquido:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(
                          +item.average_ticket_with_discounts?.value
                        )} 
                      </span>  
                    </div>`
                                                                          : ""
                                                                      }

                                                                                    ${
                                                                                      selectedOption.id ===
                                                                                      "TICKET_MEDIO_LIQUIDO"
                                                                                        ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Ticket médio líquido: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
${Currency.formatCurrency(item.average_ticket_with_discounts?.value)}
                    </span>  
                  </div>
                </div>`
                                                                                        : ""
                                                                                    }

                                                                                                  ${
                                                                                                    selectedOption.id ===
                                                                                                    "TEMPO_MEDIO_USO"
                                                                                                      ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      Tempo médio para uso: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">
${formatNumberWithOneDecimal(item.average_time_to_use.value)} dias
                    </span>  
                  </div>
                </div>`
                                                                                                      : ""
                                                                                                  }

                                                                                                  ${
                                                                                                    selectedOption.id ===
                                                                                                    "ROI_CUPOM"
                                                                                                      ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0">
                  <div>
                    <span style="color:#8a8af5;font-weight:700">
                      ROI total: 
                    </span>  
                    <span style="color:#fff;margin-left:4px;">${
                      item.roi?.value || 0
                    }
                    </span>  
                  </div>
                </div>`
                                                                                                      : ""
                                                                                                  }

              </div>
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: data.map((d) => d.date),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) =>
                [
                  "FATURAMENTO",
                  "FATURAMENTO_LIQUIDO",
                  "TICKET_MEDIO",
                  "TICKET_MEDIO_LIQUIDO",
                  "DESCONTOS_TOTAIS",
                  "CUSTOS",
                ].includes(selectedOption.id)
                  ? Currency.formatCurrency(value)
                  : Numbers.formatFloat(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: _data[selectedOptionId].series.map((name, sid, series) => {
          const stacked = !["FATURAMENTO", "TICKET_MEDIO"].includes(
            selectedOptionId
          );

          return {
            name,
            type: "bar",
            stack: stacked ? "total" : undefined,
            barWidth: stacked ? "80%" : "30%",
            data: _data[selectedOptionId].data[sid].map((d, did) =>
              totalData[did] <= 0 ? 0 : d
            ),
            label: {
              show: stacked ? sid == series.length - 1 : false,
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                const value = params.value;
                return value > 0
                  ? formatByType(value, _data[selectedOptionId].value_type)
                  : "";
              },
            },
            itemStyle: {
              color: _data[selectedOptionId].colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption, selectedOption2]);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Distribuição no período</p>
          {/* <span className={styles.info}>
            <i className="fal fa-info-circle" />
          </span> */}
        </div>
        <div className="d-flex gap-5">
          <If
            cond={["received", "used", "expired", "chargeback"].includes(
              selectedOption.id
            )}
          >
            <ComboFilters
              options={[
                { name: "Pontos", id: "total" },
                { name: "Operações", id: "count" },
              ]}
              onSelect={(option) => setSelectedOption2(option)}
              selectedOption={selectedOption2 || { name: "Valor", id: "total" }}
            />
          </If>
          <ComboFilters
            options={[
              ...(isBeforeNovember
                ? []
                : [
                    { name: "Faturamento", id: "FATURAMENTO" },
                    { name: "Ticket médio", id: "TICKET_MEDIO" },
                  ]),
              { name: "Custos", id: "CUSTOS" },
              ...(isBeforeNovember
                ? []
                : [{ name: "ROI total", id: "ROI_CUPOM" }]),
              ...(hasUnitsReportFilters
                ? []
                : [
                    {
                      name: "Tx. de conv. (Pontos x Aquisições)",
                      id: "TX_CONV_PONTOS",
                    },
                    {
                      name: "Tx. de exp. (Pontos x Aquisições)",
                      id: "TX_EXP_PONTOS",
                    },
                    {
                      name: "Tx. de conv. (Aquisições x Usos)",
                      id: "TX_CONV_AQUISICOES",
                    },
                    {
                      name: "Tx. de exp. (Aquisições x Expiração)",
                      id: "TX_EXP_AQUISICOES",
                    },
                  ]),
              { name: "Qtd. de usos de cupons", id: "QTD_USOS" },
              { name: "Tempo médio para uso", id: "TEMPO_MEDIO_USO" },
              { name: "Concessões", id: "received" },
              ...(hasUnitsReportFilters
                ? []
                : [{ name: "Consumo", id: "used" }]),
              { name: "Expirações", id: "expired" },
              { name: "Estornos", id: "chargeback" },
            ]}
            onSelect={(option) => setSelectedOption(option)}
            selectedOption={
              selectedOption || { name: "Qtd. de usos", id: "qtd_usos" }
            }
          />
        </div>
      </div>

      <div className={styles.chart}>
        {_data[selectedOption.id].data[0].every(
          (s) => s === 0 || s === null
        ) ? (
          <div className={styles.emptyChart} style={{ height: 350 }}>
            <span className="text-grey">Não há dados neste período</span>
          </div>
        ) : null}

        <div
          id="coupons-in-period-chart"
          style={{ width: "100%", height: 350 }}
        />
      </div>
    </div>
  );
}
